<style scoped>
.avatar-preview {
	width: 120px;
	height: 120px;
	object-fit: cover;
	border: 3px solid #dee2e6;
}
.bi-avatar {
	height: 128px;
	width: 128px;
}
.btn-close-white {
	opacity: 0.8;
}

.btn-close-white:hover {
	opacity: 1;
}

.card {
	border-radius: 0.5rem;
}

.badge {
	font-size: 0.9rem;
	padding: 0.5rem 0.75rem;
	display: inline-flex;
	align-items: center;
}

pre {
	background-color: #f8f9fa;
	padding: 1rem;
	border-radius: 0.375rem;
	font-size: 0.875rem;
}
</style>
<template>
	<div class="container mt-4">
		<div class="row">
			<div class="col-lg-8 mx-auto">
				<div class="card shadow-sm">
					<div class="card-header bg-primary text-white">
						<h4 class="mb-0">
							User Configuration
							<span
								class="badge bg-light text-primary ms-2"
								title="Employee id — quote this to identify the exact person"
								>ID {{ id }}</span
							>
						</h4>
					</div>
					<div v-if="userChanges" class="card-body">
						<!-- Avatar Section -->
						<div class="row mb-4">
							<div class="col-md-12">
								<label class="form-label fw-bold">Avatar</label>
								<div class="d-flex align-items-center">
									<div class="position-relative">
										<i
											v-if="
												userChanges.avatar == null || userChanges.avatar == ''
											"
											class="bi bi-avatar"
										></i>
										<img
											v-else
											:src="userChanges.avatar"
											alt="User Avatar"
											class="rounded-circle avatar-preview"
										/>
										<button
											class="btn btn-sm btn-danger position-absolute bottom-0 start-0 rounded-circle"
											@click="removeAvatar"
											type="button"
										>
											<i class="bi bi-trash"></i>
										</button>
										<button
											class="btn btn-sm btn-primary position-absolute bottom-0 end-0 rounded-circle"
											@click="triggerFileInput"
											type="button"
										>
											<i class="bi bi-camera"></i>
										</button>
									</div>
									<div class="ms-3">
										<input
											ref="fileInput"
											type="file"
											class="d-none"
											accept="image/*"
											@change="handleAvatarChange"
										/>
									</div>
								</div>
							</div>
						</div>

						<!-- Display Name -->
						<div class="row mb-3">
							<div class="col-md-6">
								<label for="displayName" class="form-label fw-bold">
									Display Name
								</label>
								<input
									id="display_name"
									v-model="userChanges.display_name"
									type="text"
									class="form-control"
									placeholder="Enter display name"
								/>
							</div>

							<!-- Username (Read-only) -->
							<div class="col-md-6">
								<label for="username" class="form-label fw-bold">
									Username
								</label>
								<input
									id="username"
									v-model="userChanges.username"
									type="text"
									class="form-control"
									readonly
									disabled
								/>
							</div>
						</div>

						<!-- User Role -->
						<div class="row mb-3">
							<div class="col-md-6">
								<label for="userRole" class="form-label fw-bold">
									User Role
								</label>
								<select
									v-if="userChanges && userChanges.role == 'root'"
									disabled
									id="role"
									class="form-select"
								>
									<option value="root">Root</option>
								</select>
								<select
									id="role"
									v-else
									v-model="userChanges.role"
									class="form-select"
								>
									<option value="">Select a role</option>
									<option
										v-for="option in optionRoles"
										:key="option.value"
										:value="option.value"
									>
										{{ option.label }}
									</option>
								</select>
							</div>

							<!-- User Status -->
							<div class="col-md-6">
								<label class="form-label fw-bold">User Status</label>
								<div class="btn-group w-100" role="group">
									<input
										type="checkbox"
										class="btn-check"
										id="isActive"
										v-model="userChanges.is_active"
									/>
									<label class="btn btn-outline-success" for="isActive">
										<i class="bi bi-check-circle"></i> Active
									</label>
								</div>
							</div>
						</div>

						<!-- User Tags -->
						<div class="row mb-4">
							<div class="col-md-12">
								<label class="form-label fw-bold">User Tags</label>
								<div class="mb-2">
									<span
										v-for="tag in userChanges.tags"
										:key="tag"
										class="badge bg-info text-dark me-2 mb-2"
									>
										{{ tag }}
										<button
											type="button"
											class="btn-close btn-close-white ms-2"
											@click="removeTag(tag)"
											style="font-size: 0.6rem"
										></button>
									</span>
									<span
										v-if="userChanges.tags.length === 0"
										class="text-muted fst-italic"
									>
										No tags added
									</span>
								</div>
								<div class="input-group">
									<select v-model="selectedTag" class="form-select">
										<option value="">Select a tag</option>
										<option
											v-for="tag in availableTags"
											:key="tag"
											:value="tag"
											:disabled="userChanges.tags.includes(tag)"
										>
											{{ tag }}
										</option>
									</select>
									<button
										class="btn btn-outline-primary"
										type="button"
										@click="addTag"
										:disabled="
											!selectedTag || userChanges.tags.includes(selectedTag)
										"
									>
										<i class="bi bi-plus-lg"></i> Add Tag
									</button>
								</div>
							</div>
						</div>

						<!-- Applicator Certification -->
						<div class="row mb-4">
							<div class="col-md-6">
								<label for="certificationNumber" class="form-label fw-bold">
									Certification Number
								</label>
								<input
									id="certificationNumber"
									v-model="userChanges.certification_number"
									type="text"
									class="form-control"
									placeholder="Enter certification number"
								/>
							</div>
							<div class="col-md-6">
								<label class="form-label fw-bold">Certification File</label>
								<div class="d-flex align-items-center gap-2">
									<input
										ref="certificationFileInput"
										type="file"
										class="d-none"
										accept="application/pdf,image/*"
										@change="handleCertificationChange"
									/>
									<button
										class="btn btn-outline-primary"
										type="button"
										@click="triggerCertificationFileInput"
									>
										<i class="bi bi-paperclip"></i>
										{{ selectedCertificationFile ? "Replace" : "Attach" }}
									</button>
									<a
										v-if="userChanges.certification"
										:href="userChanges.certification"
										target="_blank"
										rel="noopener"
										class="btn btn-outline-secondary"
									>
										<i class="bi bi-file-earmark-text"></i> View
									</a>
									<button
										v-if="userChanges.certification"
										class="btn btn-outline-danger"
										type="button"
										@click="removeCertification"
									>
										<i class="bi bi-trash"></i>
									</button>
								</div>
								<div v-if="selectedCertificationFile" class="form-text">
									{{ selectedCertificationFile.name }}
								</div>
								<div v-else class="form-text">
									PDF or image (a scan or photo).
								</div>
							</div>
						</div>

						<!-- Action Buttons -->
						<div class="row">
							<div class="col-md-12">
								<hr />
								<div class="d-flex justify-content-between align-items-center">
									<div class="d-flex gap-2 align-items-center">
										<button
											v-if="canChangePassword"
											class="btn btn-outline-warning"
											type="button"
											@click="showPasswordModal = true"
										>
											<i class="bi bi-key"></i> Change Password
										</button>
										<button
											v-if="isRoot"
											class="btn btn-outline-danger"
											type="button"
											:disabled="isRemoving"
											@click="removeUser"
										>
											<i class="bi bi-person-x"></i> Remove User
										</button>
									</div>
									<div class="ms-auto d-flex gap-2">
										<button
											class="btn btn-secondary"
											:disabled="isSaving"
											type="button"
											@click="cancelChanges"
										>
											Cancel
										</button>
										<button
											class="btn btn-primary"
											:disabled="isSaving"
											type="button"
											@click="saveChanges"
										>
											<i class="bi bi-save"></i> Save Changes
										</button>
									</div>
								</div>
							</div>
						</div>

						<!-- Password Change Modal -->
						<div
							v-if="showPasswordModal"
							class="modal d-block"
							tabindex="-1"
							style="background: rgba(0, 0, 0, 0.5)"
						>
							<div class="modal-dialog">
								<div class="modal-content">
									<div class="modal-header">
										<h5 class="modal-title">Change Password</h5>
										<button
											class="btn-close"
											type="button"
											@click="closePasswordModal"
										></button>
									</div>
									<div class="modal-body">
										<div v-if="passwordError" class="alert alert-danger">
											{{ passwordError }}
										</div>
										<div v-if="passwordSuccess" class="alert alert-success">
											Password changed successfully.
										</div>
										<div v-if="session.self?.id === props.id" class="mb-3">
											<label class="form-label">Current Password</label>
											<input
												v-model="passwordForm.current"
												class="form-control"
												type="password"
											/>
										</div>
										<div class="mb-3">
											<label class="form-label">New Password</label>
											<input
												v-model="passwordForm.new"
												class="form-control"
												minlength="8"
												type="password"
											/>
										</div>
										<div class="mb-3">
											<label class="form-label">Confirm New Password</label>
											<input
												v-model="passwordForm.confirm"
												class="form-control"
												type="password"
											/>
										</div>
									</div>
									<div class="modal-footer">
										<button
											class="btn btn-secondary"
											type="button"
											@click="closePasswordModal"
										>
											Cancel
										</button>
										<button
											class="btn btn-warning"
											:disabled="passwordSaving"
											type="button"
											@click="changePassword"
										>
											{{ passwordSaving ? "Saving..." : "Change Password" }}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-else>
						<p>loading</p>
					</div>
				</div>

				<UserAliasesCard
					:user-id="id"
					:all-organizations="isRoot"
					@merged="onMerged"
				/>
			</div>
		</div>
	</div>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRaw } from "vue";
import { useSessionStore } from "@/store/session";
import { useUserStore } from "@/store/user";
import { User, UserMergeCounts } from "@/type/api";
import { apiClient } from "@/client";
import { useRouter } from "vue-router";
import ToastNotification from "@/components/ToastNotification.vue";
import UserAliasesCard from "@/components/UserAliasesCard.vue";

interface Props {
	id: number;
}
interface Option {
	label: string;
	value: string;
}
export interface UserChanges {
	avatar: string;
	certification: string;
	certification_number: string;
	display_name: string;
	is_active: boolean;
	role: string;
	tags: string[];
	username: string;
}
const fileInput = ref<HTMLInputElement | null>(null);
const isSaving = ref<boolean>(false);
const props = defineProps<Props>();
const selectedFile = ref<File | null>(null);
const certificationFileInput = ref<HTMLInputElement | null>(null);
const selectedCertificationFile = ref<File | null>(null);
const selectedTag = ref<string>("");
const userStore = useUserStore();
const session = useSessionStore();
const userChanges = ref<UserChanges>();
const router = useRouter();
const isRemoving = ref(false);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

// onMerged follows the merge to the surviving member: this page's member is
// retired by the merge, so staying here would show a row that no longer answers
// to its own id.
function onMerged(intoUserId: number, counts: UserMergeCounts) {
	showToast(
		"Merged",
		`Moved ${counts.aliases} alias(es) and ${counts.records.toLocaleString()} record(s).`,
	);
	router.push(`/_/configuration/user/${intoUserId}`);
}

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
	setTimeout(() => {
		toastShow.value = false;
	}, 5000);
}

const isRoot = computed(() => session.self?.role === "root");
const canChangePassword = computed(
	() => isRoot.value || session.self?.id === props.id,
);

const showPasswordModal = ref(false);
const passwordError = ref("");
const passwordSuccess = ref(false);
const passwordSaving = ref(false);
const passwordForm = ref({
	current: "",
	new: "",
	confirm: "",
});

function closePasswordModal() {
	showPasswordModal.value = false;
	passwordError.value = "";
	passwordSuccess.value = false;
	passwordForm.value = { current: "", new: "", confirm: "" };
}

async function changePassword() {
	passwordError.value = "";
	passwordSuccess.value = false;

	if (!passwordForm.value.new || passwordForm.value.new.length < 8) {
		passwordError.value = "New password must be at least 8 characters.";
		return;
	}
	if (passwordForm.value.new !== passwordForm.value.confirm) {
		passwordError.value = "Passwords do not match.";
		return;
	}

	passwordSaving.value = true;
	try {
		const payload: Record<string, string> = {
			new_password: passwordForm.value.new,
		};
		if (!isRoot.value || session.self?.id === props.id) {
			payload.current_password = passwordForm.value.current;
		}
		await apiClient.JSONPost(`/api/user/${props.id}/password`, payload);
		passwordSuccess.value = true;
		passwordForm.value = { current: "", new: "", confirm: "" };
	} catch (e: any) {
		const msg =
			e?.response?.data?.message || e?.message || "Failed to change password.";
		passwordError.value = msg;
	} finally {
		passwordSaving.value = false;
	}
}

const optionRoles = computed<Option[]>(() => {
	const options: Option[] = [
		{ value: "account-owner", label: "Account Owner" },
		{ value: "manager", label: "Manager" },
		{ value: "tech1", label: "Tech 1" },
		{ value: "tech2", label: "Tech 2" },
		{ value: "tech3", label: "Tech 3" },
	];
	if (isRoot.value) {
		options.push({ value: "staff", label: "Staff" });
	}
	return options;
});

const availableTags: string[] = ["warrant", "drone pilot"];

const triggerFileInput = () => {
	fileInput.value?.click();
};

const handleAvatarChange = (event: Event) => {
	const target = event.target as HTMLInputElement;
	const file = target.files?.[0];

	if (file) {
		selectedFile.value = file;
		const reader = new FileReader();
		reader.onload = (e) => {
			if (userChanges.value == undefined) {
				console.log("can't update userChanges, it's undefined");
				return;
			}
			userChanges.value.avatar = e.target?.result as string;
		};
		reader.readAsDataURL(file);
	}
};

const removeAvatar = () => {
	if (userChanges.value == undefined) {
		return;
	}
	userChanges.value.avatar = "";
	if (fileInput.value) {
		fileInput.value.value = "";
	}
};

const triggerCertificationFileInput = () => {
	certificationFileInput.value?.click();
};

const handleCertificationChange = (event: Event) => {
	const target = event.target as HTMLInputElement;
	const file = target.files?.[0];
	if (file) {
		selectedCertificationFile.value = file;
	}
};

const removeCertification = () => {
	if (userChanges.value == undefined) {
		return;
	}
	userChanges.value.certification = "";
	selectedCertificationFile.value = null;
	if (certificationFileInput.value) {
		certificationFileInput.value.value = "";
	}
};

const addTag = () => {
	if (userChanges.value == null) {
		return;
	}
	if (
		selectedTag.value &&
		!userChanges.value.tags.includes(selectedTag.value)
	) {
		userChanges.value.tags.push(selectedTag.value);
		selectedTag.value = "";
	}
};

const removeTag = (tag: string) => {
	if (userChanges.value == null) {
		return;
	}
	const index = userChanges.value.tags.indexOf(tag);
	if (index > -1) {
		userChanges.value.tags.splice(index, 1);
	}
};

async function removeUser() {
	const username = userChanges.value?.username ?? "";
	if (!confirm(`Remove user "${username}"? This action cannot be undone.`)) {
		return;
	}
	isRemoving.value = true;
	try {
		await apiClient.JSONPut(`/api/user/${props.id}`, {
			deleted_at: new Date().toISOString(),
		});
		await router.push("/_/configuration/user");
	} catch (e: any) {
		const msg =
			e?.response?.data?.message || e?.message || "Failed to remove user.";
		showToast("Remove User Failed", msg);
	} finally {
		isRemoving.value = false;
	}
}

interface UserRequestPut {
	avatar?: string | null;
	certification?: string | null;
	certification_number?: string | null;
	display_name?: string;
	is_active?: boolean;
	role?: string;
	tags?: string[];
}
const seedUserChanges = (u: User) => {
	userChanges.value = {
		avatar: u.avatar,
		certification: u.certification ?? "",
		certification_number: u.certification_number ?? "",
		display_name: u.display_name,
		is_active: u.is_active,
		role: u.role,
		tags: structuredClone(toRaw(u.tags)),
		username: u.username,
	};
};
const saveChanges = async () => {
	const uc = userChanges.value;
	if (!uc) {
		console.log("empty user changes");
		return;
	}
	console.log("Saving user changes");
	isSaving.value = true;
	const all_users = await userStore.withAll(isRoot.value);
	const u = all_users.find((u: User) => u.id == props.id);
	if (!u) {
		console.log("no matching user");
		isSaving.value = false;
		return;
	}
	let payload: UserRequestPut = {};
	if (uc.avatar != u.avatar) {
		if (selectedFile.value) {
			try {
				const formData = new FormData();
				formData.append("file", selectedFile.value);

				const url = session.urls?.api.avatar;
				if (!url) {
					throw new Error("avatar upload url is not configured");
				}
				const response = await fetch(url, {
					body: formData,
					method: "POST",
				});
				if (!response.ok) {
					throw new Error(`Upload failed: ${response.statusText}`);
				}

				const data = await response.json();
				payload.avatar = data.uri;
			} catch (error: any) {
				console.error("Failed to upload avatar", error);
				showToast(
					"Avatar Upload Failed",
					error?.message ?? "Failed to upload avatar.",
				);
				isSaving.value = false;
				return;
			}
		} else if (!uc.avatar) {
			payload.avatar = null;
		}
	}
	if (uc.certification != u.certification) {
		if (selectedCertificationFile.value) {
			try {
				const formData = new FormData();
				formData.append("file", selectedCertificationFile.value);

				const url = session.urls?.api.certification;
				if (!url) {
					throw new Error("certification upload url is not configured");
				}
				const response = await fetch(url, {
					body: formData,
					method: "POST",
				});
				if (!response.ok) {
					throw new Error(`Upload failed: ${response.statusText}`);
				}

				const data = await response.json();
				payload.certification = data.uri;
			} catch (error: any) {
				console.error("Failed to upload certification", error);
				showToast(
					"Certification Upload Failed",
					error?.message ?? "Failed to upload certification file.",
				);
				isSaving.value = false;
				return;
			}
		} else if (!uc.certification) {
			payload.certification = null;
		}
	}
	const certificationNumber = uc.certification_number.trim();
	if (certificationNumber !== (u.certification_number ?? "")) {
		payload.certification_number =
			certificationNumber === "" ? null : certificationNumber;
	}
	if (uc.display_name != u.display_name) {
		payload.display_name = uc.display_name;
	}
	if (uc.is_active != u.is_active) {
		payload.is_active = uc.is_active;
	}
	if (uc.role != u.role) {
		payload.role = uc.role;
	}
	if (uc.tags != u.tags) {
		payload.tags = uc.tags;
	}
	if (Object.keys(payload).length === 0) {
		showToast("Nothing to Save", "No changes were detected.");
		isSaving.value = false;
		return;
	}
	const url = u.uri;
	try {
		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		});
		if (!response.ok) {
			let message = `HTTP error! status: ${response.status}`;
			try {
				const errorData = await response.json();
				message = errorData.message || message;
			} catch {
				// non-JSON error body; keep the status message
			}
			throw new Error(message);
		}
		const users = await userStore.fetchAll(isRoot.value);
		const refreshed = users.find((candidate: User) => candidate.id == props.id);
		if (refreshed) {
			seedUserChanges(refreshed);
		}
		selectedFile.value = null;
		selectedCertificationFile.value = null;
		showToast("Changes Saved", "The employee record was updated.");
	} catch (error: any) {
		console.error("Failed to save user changes", error);
		showToast("Save Failed", error?.message ?? "Failed to save changes.");
	} finally {
		isSaving.value = false;
	}
};

const cancelChanges = () => {
	// Implement cancel/reset logic here
	console.log("Canceling changes");
	if (
		confirm(
			"Are you sure you want to cancel? All unsaved changes will be lost.",
		)
	) {
		// Reset to original values or navigate away
		window.history.back();
	}
};
onMounted(() => {
	userStore.withAll(isRoot.value).then((users) => {
		console.log("got users. looking for match", users, props.id);
		for (const u of users) {
			if (u.id == props.id) {
				seedUserChanges(u);
				console.log("User set to", u);
			}
		}
	});
});
</script>
